// src/pages/NeighborhoodPage.js
import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, LoadScriptNext, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { motion, AnimatePresence } from 'framer-motion';
import exteriorAlt from '../assets/reading-towers/gallery/exterior-dsc4870-1400.jpg';
import northView from '../assets/reading-towers/gallery/north-view-1400.jpg';
import southView from '../assets/reading-towers/gallery/south-view-1400.jpg';
import poiCategoriesData from '../data/poiCategories.json';

// Map Styles (Unchanged)
const mapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];


const readingTowersPosition = { lat: 40.335363, lng: -75.925737 };
const readingTowersAddress = "601 Penn Street, Reading, PA 19601";
const primaryColor = "text-teal-700";

const NeighborhoodPage = () => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [activeMarker, setActiveMarker] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(poiCategoriesData[0]?.name || null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const mapRef = useRef(null);

  const handleMarkerClick = (poi) => {
    setActiveMarker(poi);
    if (mapRef.current && poi.lat && poi.lng) {
      const targetLat = poi.name === "Reading Tower Apartments" ? poi.lat + 0.001 : poi.lat + 0.0005;
      mapRef.current.panTo({ lat: targetLat, lng: poi.lng });
       if (mapRef.current.getZoom() < 16) {
        mapRef.current.setZoom(16);
      }
    }
  };
  const handleInfoWindowClose = () => setActiveMarker(null);
  const toggleAccordion = (categoryName) => setOpenAccordion(openAccordion === categoryName ? null : categoryName);
  
  // CORRECTED THIS LINE:
  const onMapLoad = (map) => {
    mapRef.current = map;
    setIsMapLoaded(true);
  };

  useEffect(() => {
    if (!openAccordion && poiCategoriesData.length > 0) {
      setOpenAccordion(poiCategoriesData[0].name);
    }
  }, [openAccordion]);


  if (!apiKey) return (
    <div className="container mx-auto p-10 text-center">
      <h1 className="text-2xl font-bold text-red-600 mb-4">Map Error</h1>
      <p className="text-lg text-gray-700">
        The Google Maps API Key is missing. Please ensure it's set correctly in your{' '}
        <code>.env</code> file and that you've restarted the development server.
      </p>
    </div>
  );

  const buildingCustomSvgPin = (fillColor = "#0F766E", strokeColor = "#FFFFFF") => {
    return {
        path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
        fillColor: fillColor, fillOpacity: 1, strokeColor: strokeColor, strokeWeight: 1,
        rotation: 0, scale: 1.7, anchor: new window.google.maps.Point(12, 24),
    };
  };

  // Helper function to determine InfoWindow background color
  const getInfoWindowBackgroundColor = (marker) => {
    if (!marker) return '#374151'; // Default gray for safety
    if (marker.isBuilding) {
      return '#0D9488'; // Tailwind's teal-600 hex
    }
    if (marker.categoryColor) {
      // categoryColor is already the hex string without '#'
      return `#${marker.categoryColor}`;
    }
    return '#374151'; // Tailwind's gray-700 hex (fallback)
  };

  return (
    <LoadScriptNext googleMapsApiKey={apiKey} libraries={['marker']}>
      <div className="bg-white">
        <section className="w-full">
          <div className="flex flex-col lg:flex-row lg:h-[75vh]">
            <div className="w-full lg:w-2/3 h-[60vh] lg:h-full">
              {apiKey ? (
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={readingTowersPosition}
                  zoom={16}
                  options={{ styles: mapStyles, disableDefaultUI: true, zoomControl: true, fullscreenControl: true, mapTypeControl: false, streetViewControl: false }}
                  onLoad={onMapLoad}
                >
                  {isMapLoaded && (() => {
                    const createCircleMarkerIcon = (color) => ({
                      path: 'M 0, 0 m -7, 0 a 7,7 0 1,0 14,0 a 7,7 0 1,0 -14,0',
                      fillColor: color, fillOpacity: 0.9, strokeWeight: 1, strokeColor: "#FFFFFF",
                      rotation: 0, scale: 1.1, anchor: new window.google.maps.Point(0, 0),
                    });

                    return (
                      <>
                        <MarkerF
                          position={readingTowersPosition}
                          title="Reading Tower Apartments"
                          onClick={() => handleMarkerClick({ name: "Reading Tower Apartments", address: readingTowersAddress, lat: readingTowersPosition.lat, lng: readingTowersPosition.lng, isBuilding: true })}
                          icon={buildingCustomSvgPin()}
                          zIndex={100}
                        />
                        {poiCategoriesData.flatMap(category =>
                          category.items.map(poi => (
                            <MarkerF
                              key={poi.id}
                              position={{ lat: poi.lat, lng: poi.lng }}
                              title={poi.name}
                              icon={createCircleMarkerIcon(category.iconColor)}
                              onClick={() => handleMarkerClick({...poi, categoryColor: category.iconColor.replace("#", "")})}
                            />
                          ))
                        )}
                      </>
                    );
                  })()}
                  {isMapLoaded && activeMarker && (
                    <InfoWindowF
                      position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
                      onCloseClick={handleInfoWindowClose}
                      options={{
                        pixelOffset: new window.google.maps.Size(0, activeMarker.isBuilding ? -40 : -20),
                        disableAutoPan: false,
                      }}
                    >
                     <div
                        className="p-2 text-white rounded shadow-lg max-w-[220px] text-center"
                        style={{ backgroundColor: getInfoWindowBackgroundColor(activeMarker) }}
                      >
                        <h4 className="font-bold text-sm leading-tight mb-0.5">{activeMarker.name}</h4>
                        <p className="text-xs leading-snug">{activeMarker.address}</p>
                      </div>
                    </InfoWindowF>
                  )}
                </GoogleMap>
              ) : ( <div className="w-full h-full flex items-center justify-center bg-gray-200"><p>Loading map...</p></div> )}
            </div>
            {/* POI List Area */}
            <div className="w-full lg:w-1/3 bg-white p-4 md:p-6 shadow-lg lg:h-full lg:overflow-y-auto">
              {poiCategoriesData.map((category) => (
                <div key={category.name} className="mb-px border-b border-gray-200 last:border-b-0">
                  <button onClick={() => toggleAccordion(category.name)} className="w-full flex items-center justify-between py-3.5 px-2 text-left text-gray-700 hover:bg-gray-50 transition-colors duration-150 focus:outline-none">
                    <div className="flex items-center">
                      <span className={`flex-shrink-0 w-2.5 h-2.5 rounded-full mr-3 ${category.tailwindIconColor}`}></span>
                      <span className="font-medium text-sm text-gray-800">{category.name}</span>
                    </div>
                    <motion.span animate={{ rotate: openAccordion === category.name ? 180 : 0 }} transition={{ duration: 0.2 }}>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                    </motion.span>
                  </button>
                  <AnimatePresence>
                    {openAccordion === category.name && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: "circOut" }} className="overflow-hidden pl-3.5 pr-1">
                        <ul className="pt-1 pb-2">
                          {category.items.map((item) => (
                            <li key={item.id} className="border-b border-dotted border-gray-200 last:border-b-0">
                               <button onClick={() => handleMarkerClick({...item, categoryColor: category.iconColor.replace("#", "")})} className="w-full text-left py-2.5 px-1.5 hover:bg-gray-50 rounded-sm transition-colors group">
                                <div className="flex justify-between items-start">
                                  <span className={`text-xs font-medium text-gray-700 group-hover:text-[${category.iconColor}] mr-2`}>{item.name}</span>
                                  <span className="text-xs text-gray-500 text-right flex-shrink-0">{item.address}</span>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="max-w-3xl mx-auto text-center md:text-left">
            <h2 className={`text-3xl sm:text-4xl font-bold ${primaryColor} leading-tight mb-6`}>
              Downtown Reading: Central, Practical, Close To Local Destinations
            </h2>
            <p className="text-lg leading-relaxed text-gray-600 mb-4">
              Reading Tower is located at 601 Penn Street in Downtown Reading, near restaurants, local businesses, Santander Arena, and public transportation options. The location gives residents a central home base without overstating what is available directly inside the building.
            </p>
            <p className="text-lg leading-relaxed text-gray-600 mb-4">
              Nearby Penn Street destinations include dining, entertainment, local businesses, and parking options. BARTA provides public bus service throughout Reading and Berks County, with downtown transit access nearby.
            </p>
            <p className="text-lg leading-relaxed text-gray-600 mb-4">
              For residents who want a central Reading address with updated apartments and everyday conveniences, Reading Tower offers a practical place to call home.
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 mb-6 md:mb-8">
              <div className="md:col-span-3">
                <img
                  src={exteriorAlt}
                  alt="Reading Tower exterior"
                  className="rounded-lg shadow-xl w-full h-72 sm:h-80 md:h-96 object-cover"
                />
              </div>
              <div className="md:col-span-2">
                <img
                  src={southView}
                  alt="View from Reading Tower"
                  className="rounded-lg shadow-xl w-full h-72 sm:h-80 md:h-96 object-cover"
                />
              </div>
            </div>
            <div className="w-full">
              <img
                src={northView}
                alt="View from Reading Tower"
                className="rounded-lg shadow-xl w-full h-80 sm:h-96 md:h-[500px] object-cover"
              />
            </div>
          </div>
        </section>
      </div>
    </LoadScriptNext>
  );
};

export default NeighborhoodPage;
